import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ScienceIcon from '@mui/icons-material/Science'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import baseUrl from '../../api/baseUrl'
import getDecryptedToken from '../../api/getDecryptedToken'
import LabOrderForm, { type ClinicDetails } from '../../components/labs/LabOrderForm'
import {
  buildClinicOrderPayload,
  clinicSenderDefaults,
  emptyLabOrderValues,
  type LabOrderFormValues,
  type LabPriceListItem,
} from '../../components/labs/labOrderFormUtils'
import {
  formatPatientLabel,
  formatPatientMeta,
} from '../../components/labs/labPatientUtils'

type Partner = {
  id: string
  labId: string | null
  name: string
  address?: string
  phone?: string
  canSendOrders?: boolean
  canTrackOrders?: boolean
  isShadow?: boolean
  status?: string
}

function partnerUsable(p: Partner) {
  return (
    (p.labId && (p.canSendOrders ?? p.status === 'active')) ||
    (p.isShadow && (p.canTrackOrders ?? p.status === 'active'))
  )
}

function SendLabOrder() {
  const [partners, setPartners] = useState<Partner[]>([])
  const [partnersLoading, setPartnersLoading] = useState(true)
  const clinicDetails: ClinicDetails = {
    name: getDecryptedToken('clinicName') || 'Clinic',
    phone: getDecryptedToken('clinicPhoneNumber') || undefined,
    address: getDecryptedToken('address') || undefined,
  }

  const [values, setValues] = useState<LabOrderFormValues>(() => ({
    ...emptyLabOrderValues(),
    ...clinicSenderDefaults(clinicDetails),
  }))
  const [priceListItems, setPriceListItems] = useState<LabPriceListItem[]>([])
  const [priceListLoading, setPriceListLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [patientError, setPatientError] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectedLabId = searchParams.get('labId')
  const preselectedPartnerId = searchParams.get('partnerId')
  const preselectedPatientId = searchParams.get('patientId')

  useEffect(() => {
    if (!preselectedPatientId) return
    baseUrl
      .get('patient/findById', { headers: { patientid: preselectedPatientId } })
      .then((res) => {
        const p = res.data?.data
        if (!p) return
        setValues((prev) => ({
          ...prev,
          patient: formatPatientLabel(p),
          patientMeta: formatPatientMeta(p),
          caseTitle:
            prev.caseTitle ||
            (p.regNo?.trim() ? `Case — ${p.regNo.trim()}` : prev.caseTitle),
        }))
      })
      .catch(() => {})
  }, [preselectedPatientId])

  useEffect(() => {
    setPartnersLoading(true)
    baseUrl
      .get('client/labs/partners')
      .then((res) => {
        const list = (res.data.data ?? []).filter(partnerUsable)
        setPartners(list)
        const pick =
          (preselectedPartnerId &&
            list.find((p: Partner) => p.id === preselectedPartnerId)) ||
          (preselectedLabId &&
            list.find((p: Partner) => String(p.labId) === preselectedLabId)) ||
          list[0]
        if (pick) {
          setValues((prev) => ({
            ...prev,
            labId: pick.labId ? String(pick.labId) : '',
            labPartnerId: pick.id,
          }))
        }
      })
      .catch(() => setPartners([]))
      .finally(() => setPartnersLoading(false))
  }, [preselectedLabId, preselectedPartnerId])

  const loadPriceList = useCallback((labId: string) => {
    if (!labId) {
      setPriceListItems([])
      return
    }
    setPriceListLoading(true)
    baseUrl
      .get('client/labs/items', { params: { labId } })
      .then((res) => setPriceListItems(res.data.data ?? []))
      .catch(() => setPriceListItems([]))
      .finally(() => setPriceListLoading(false))
  }, [])

  useEffect(() => {
    loadPriceList(values.labId)
  }, [values.labId, loadPriceList])

  useEffect(() => {
    if (values.patient.trim()) setPatientError(false)
  }, [values.patient])

  const selectedPartner = partners.find((p) => p.id === values.labPartnerId)
  const isLocalOrder = !!selectedPartner?.isShadow

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!values.patient.trim()) {
      setPatientError(true)
      setError('Select or add a patient')
      return
    }
    setPatientError(false)
    if (!values.labPartnerId || !values.caseTitle.trim()) {
      setError('Select a lab and enter case details')
      return
    }
    if (!values.toothNos.length) {
      setError('Select at least one tooth')
      return
    }
    if (!values.invoiceItems.some((row) => row.item.trim())) {
      setError('Add at least one lab item')
      return
    }
    setSubmitting(true)
    setError('')
    baseUrl
      .post('client/labs/orders', buildClinicOrderPayload(values))
      .then(() => navigate('/client/labs/orders'))
      .catch((err) =>
        setError(err?.response?.data?.message || 'Failed to save order'),
      )
      .finally(() => setSubmitting(false))
  }

  const partnerOptions = partners.map((p) => ({
    id: p.id,
    labId: p.labId,
    name: p.name,
    address: p.address,
    phone: p.phone,
    isLocal: p.isShadow,
  }))

  return (
    <div className="flex min-h-[calc(100dvh-10rem)] w-full flex-col gap-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to="/client/labs/orders"
            className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-primary"
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to lab orders
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primaryLight text-white shadow-md shadow-primary/20">
              <ScienceIcon sx={{ fontSize: 22 }} />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100 md:text-xl">
                {isLocalOrder ? 'Track lab order' : 'Send lab order'}
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">
                {isLocalOrder
                  ? 'Create a private order record for your personal lab. Only your clinic can see and update it.'
                  : 'Fill in case details and items — the order is sent to your partner lab on the platform.'}
              </p>
            </div>
          </div>
        </div>

        {selectedPartner ? (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Selected lab
            </p>
            <p className="mt-0.5 font-semibold text-slate-900 dark:text-slate-100">
              {selectedPartner.name}
            </p>
            <span
              className={
                isLocalOrder
                  ? 'mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                  : 'mt-2 inline-flex rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
              }
            >
              {isLocalOrder ? 'Self-tracked' : 'Platform lab'}
            </span>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {partnersLoading ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-700">
            <div className="h-5 w-40 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
          </div>
          <div className="space-y-4 p-6">
            <div className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700/60" />
            <div className="h-32 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700/60" />
            <div className="h-24 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700/60" />
          </div>
        </div>
      ) : partners.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center shadow-sm dark:border-slate-600 dark:bg-slate-800">
          <div className="flex size-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-700">
            <ScienceIcon sx={{ fontSize: 28 }} />
          </div>
          <p className="mt-4 text-sm font-medium text-slate-800 dark:text-slate-200">
            No partner labs yet
          </p>
          <p className="mt-1 max-w-sm text-sm text-slate-500">
            Add a registered lab from the directory or create a personal lab to
            track orders yourself.
          </p>
          <Link
            to="/client/labs/partners"
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95"
          >
            Go to partner labs
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/80 sm:px-6">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Order details
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {isLocalOrder
                ? 'Add items manually and save — you manage status and payments later.'
                : 'Items and prices come from the lab catalog when available.'}
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <LabOrderForm
              partners={partnerOptions}
              values={values}
              onChange={setValues}
              onSubmit={submit}
              submitting={submitting}
              submitLabel={isLocalOrder ? 'Save for tracking' : 'Send to lab'}
              submittingLabel={isLocalOrder ? 'Saving…' : 'Sending…'}
              onCancel={() => navigate('/client/labs/orders')}
              clinicDetails={clinicDetails}
              priceListItems={priceListItems}
              priceListLoading={priceListLoading}
              patientError={patientError}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default SendLabOrder
